import { useCallback, useEffect, useRef, useState } from "react";
import { collectDeviceFingerprint } from "./fingerprintCollector";

/**
 * Collects the device fingerprint once per mount and hands back a getter.
 *
 * Two callers use it, both in Code.js and both posting to /docs/verify/{id}:
 * `checkUserSession` (returning reader with an existing session) and
 * `verifyCode` (passcode submit). No other request carries the fingerprint.
 *
 * Why a ref and not just state: the request handlers in Code.js are closures
 * created at render time. Reading `fingerprint` from state inside one of them
 * would capture whatever the value was when that closure was made - usually
 * null, because collection has not finished yet on first render. The ref is
 * read at call time, so it always sees the latest value.
 *
 * If collection has not finished, `getFingerprint()` returns null and the
 * request goes out without it - authorization must never wait on, or fail
 * because of, fingerprinting.
 *
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] set false to skip collection
 *        entirely (feature flag / consent gate).
 * @returns {{ getFingerprint: () => object|null, fingerprint: object|null, ready: boolean }}
 */
export default function useDeviceFingerprint(options = {}) {
  const { enabled = true } = options;

  const fingerprintRef = useRef(null);
  const [fingerprint, setFingerprint] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setReady(true);
      return undefined;
    }

    let cancelled = false;

    collectDeviceFingerprint()
      .then((result) => {
        if (cancelled) return;
        fingerprintRef.current = result;
        setFingerprint(result);
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        // collectDeviceFingerprint does not reject, but never let a surprise
        // here leave the reader stuck.
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  /** Latest fingerprint, or null if collection has not finished yet. */
  const getFingerprint = useCallback(() => fingerprintRef.current, []);

  return { getFingerprint, fingerprint, ready };
}
